import { useState, useEffect, lazy, Suspense, useRef, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import SEOHead from "@/components/SEOHead";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import DesktopFloatingCTA from "@/components/DesktopFloatingCTA";
import { analytics } from "@/lib/analytics";

// Lazy load below-fold sections
const IndustryBubbleMarquee = lazy(() => import("@/components/IndustryBubbleMarquee"));
const ContentPartnerStrip = lazy(() => import("@/components/ContentPartnerStrip"));
const ROICalculator = lazy(() => import("@/components/ROICalculator"));
const FormatDeepDive = lazy(() => import("@/components/FormatDeepDive"));
const HomePricing = lazy(() => import("@/components/HomePricing"));
const VideoShowcase = lazy(() => import("@/components/VideoShowcase"));
const WhyUsSection = lazy(() => import("@/components/WhyUsSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const FinalPushSection = lazy(() => import("@/components/FinalPushSection"));
const Footer = lazy(() => import("@/components/Footer"));

const SectionSkeleton = ({ height = "h-96" }: { height?: string }) => (
  <div className={`${height} w-full animate-pulse bg-gradient-to-b from-background/50 to-background/20`} />
);

const Index = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const trackedSections = useRef<Set<string>>(new Set());
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const timer1 = setTimeout(() => setIsLoaded(true), 50);
    const timer2 = setTimeout(() => setShowContent(true), 150);
    analytics.pageView('homepage');
    return () => { clearTimeout(timer1); clearTimeout(timer2); };
  }, []);

  const handleScrollStateChange = useCallback(() => {
    document.body.classList.add('is-scrolling');
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      document.body.classList.remove('is-scrolling');
    }, 150);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScrollStateChange, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScrollStateChange);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [handleScrollStateChange]);

  useEffect(() => {
    const sections = ['hero', 'industries', 'formats', 'pricing', 'showcase', 'why-us', 'faq', 'final-push'];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('data-section');
            if (sectionId && !trackedSections.current.has(sectionId)) {
              trackedSections.current.add(sectionId);
              const scrollDepth = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
              analytics.sectionViewed(sectionId, scrollDepth);
            }
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -10% 0px' }
    );

    const timer = setTimeout(() => {
      sections.forEach((section) => {
        const el = document.querySelector(`[data-section="${section}"]`);
        if (el) observer.observe(el);
      });
    }, 500);

    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);

  return (
    <>
      <SEOHead />
      
      {/* Curtain reveal */}
      <div 
        className={`fixed inset-0 z-[100] bg-background pointer-events-none transition-all duration-500 ease-out ${
          isLoaded ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ clipPath: isLoaded ? 'inset(100% 0 0 0)' : 'inset(0 0 0 0)' }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          <div className="text-4xl animate-flame-flicker">🚀</div>
          <div className="w-32 h-1 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-neon-purple to-neon-pink animate-fuel-loader" />
          </div>
          <span className="text-sm text-muted-foreground animate-pulse">Fueling up...</span>
        </div>
      </div>
      
      <Navbar />
      
      <main className={`min-h-screen gradient-mesh transition-opacity duration-300 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
        {/* 1. Hero — hook them */}
        <div className={showContent ? 'animate-section-entrance' : ''} data-section="hero">
          <Hero />
        </div>

        {/* 2. Industry Bubbles — instant trust */}
        <Suspense fallback={<SectionSkeleton height="h-32" />}>
          <div data-section="industries">
            <IndustryBubbleMarquee />
          </div>
        </Suspense>

        {/* 2b. Content Partner strip — clear positioning */}
        <Suspense fallback={<SectionSkeleton height="h-24" />}>
          <ContentPartnerStrip />
        </Suspense>

        {/* 3. Video Showcase — real proof, moved up so visitors see videos immediately */}
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '50ms' }} data-section="showcase">
            <VideoShowcase />
          </div>
        </Suspense>

        {/* 4. Format Deep Dive — Short / Long / Viral Edits */}
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '80ms' }} data-section="formats">
            <FormatDeepDive />
          </div>
        </Suspense>

        {/* 5. Pricing — clear investment */}
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '110ms' }} data-section="pricing">
            <HomePricing />
          </div>
        </Suspense>

        {/* 6. ROI Calculator — let them see their own potential */}
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '140ms' }} data-section="roi-calculator">
            <ROICalculator />
          </div>
        </Suspense>
        
        {/* 6. Why Us — handle objections */}
        <Suspense fallback={<SectionSkeleton height="h-32" />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '150ms' }} data-section="why-us">
            <WhyUsSection />
          </div>
        </Suspense>
        
        {/* 6. FAQ — final objections */}
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '200ms' }} data-section="faq">
            <FAQSection />
          </div>
        </Suspense>
        
        {/* 7. Final WhatsApp CTA — close the deal */}
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '250ms' }} data-section="final-push">
            <FinalPushSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton height="h-48" />}>
          <Footer />
        </Suspense>
        
        <StickyMobileCTA />
        <DesktopFloatingCTA />
      </main>
    </>
  );
};

export default Index;
